/**
 * Resource Repository
 */

import { BaseRepository } from './BaseRepository';
import { Resource } from '../types/WorkflowEntities';

class ResourceRepository extends BaseRepository<Resource> {
    protected entityName = 'resource';
    protected graphQLType = 'Resource';

    // ✅ FERP SAFETY: Mandatory repository pattern method with tenant isolation
    async findByTenantAndId(tenantId: string, id: string): Promise<Resource | null> {
        await this.validateRepositorySafety({
            operation: 'FIND_BY_TENANT_AND_ID',
            domain: 'OPERATIONAL',
            tenantId,
            entityType: 'Resource',
            criticalityLevel: 'HIGH', // Resources are critical for capacity planning
            metadata: { id, resourceRepository: true }
        });

        const query = `
            query GetResourceByTenantAndId($id: String!, $tenant: String!) {
                resource(id: $id, tenant: $tenant) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        try {
            const result = await this.query<{ resource: Resource | null }>(query, {
                id,
                tenant: tenantId
            }, {
                cache: true,
                cacheTime: 5 * 60 * 1000 // 5 minutes cache for resources
            });

            const resource = result.resource || null;

            // ✅ FERP SAFETY: Log successful operation with resource details
            if (this.auditTrail) {
                await this.auditTrail.logOperationSuccess({
                    operation: 'FIND_BY_TENANT_AND_ID',
                    tenantId,
                    entityType: 'Resource',
                    entityId: id,
                    found: !!resource,
                    resourceType: resource?.type,
                    resourceStatus: resource?.status,
                    resourceCapacity: resource?.capacity?.total,
                    timestamp: Date.now()
                });
            }

            return resource;
        } catch (error) {
            // ✅ FERP SAFETY: Log failed operation
            if (this.auditTrail) {
                await this.auditTrail.logOperationFailure({
                    operation: 'FIND_BY_TENANT_AND_ID',
                    tenantId,
                    entityType: 'Resource',
                    entityId: id,
                    error: error instanceof Error ? error.message : String(error),
                    timestamp: Date.now()
                });
            }
            throw error;
        }
    }

    // Legacy findById method - redirects to findByTenantAndId
    async findById(resourceId: string): Promise<Resource | null> {
        return this.findByTenantAndId(this.tenant, resourceId);
    }

    // Resource-specific methods
    async getUtilization(resourceId?: string): Promise<any> {
        const query = resourceId ? `
            query GetResourceUtilization($resourceId: ID!) {
                resourceUtilization(resourceId: $resourceId) {
                    resourceId
                    current
                    peak
                    average
                    timestamp
                }
            }
        ` : `
            query GetAllResourceUtilization {
                resourceUtilization {
                    resourceId
                    current
                    peak
                    average
                    timestamp
                }
            }
        `;

        const variables = resourceId ? { resourceId } : {};
        return this.executeQuery(query, variables);
    }

    async findByType(type: string): Promise<Resource[]> {
        const query = `
            query FindResourcesByType($type: String!) {
                resources(filter: { type: $type }) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        const result = await this.executeQuery(query, { type });
        return result.resources || [];
    }

    async findAvailable(): Promise<Resource[]> {
        const query = `
            query FindAvailableResources {
                resources(filter: { status: "available" }) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        const result = await this.executeQuery(query, {});
        return result.resources || [];
    }

    async allocate(resourceId: string, amount: number): Promise<any> {
        const mutation = `
            mutation AllocateResource($resourceId: ID!, $amount: Float!) {
                allocateResource(resourceId: $resourceId, amount: $amount) {
                    success
                    allocated
                    remaining
                }
            }
        `;

        return this.executeQuery(mutation, { resourceId, amount });
    }

    async release(resourceId: string, amount: number): Promise<any> {
        const mutation = `
            mutation ReleaseResource($resourceId: ID!, $amount: Float!) {
                releaseResource(resourceId: $resourceId, amount: $amount) {
                    success
                    released
                    available
                }
            }
        `;

        return this.executeQuery(mutation, { resourceId, amount });
    }

    protected getFieldSelection(): string {
        return `
            id
            name
            type
            capacity {
                total
                unit
                reserved
                available
            }
            usage {
                cpu
                memory
                disk
                network
            }
            status
            location
            tags
            cost {
                hourly
                monthly
                currency
            }
            createdAt
            updatedAt
        `;
    }
}

export const resourceRepository = new ResourceRepository();
export { ResourceRepository };